import { AxiosError, AxiosResponse, isCancel } from 'axios';
import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type {
  ExtendedInitiative,
  FocusData,
  Initiative,
  InitiativeTaskResponse,
  InitiativeTasksFilters,
  KPIOverviewData,
  PreSaveInitiative,
} from '../initiative-client/initiative-client.type';

const axiosInstance = AxiosInstance();

/**
 * Fetches array of initiatives for a given portfolio id
 * @param portfolio_id - portfolio id
 * @returns Array of initiatives
 */
async function getInitiatives(portfolio_id: string, signal?: AbortSignal): Promise<Initiative[]> {
  try {
    const url = appConfig.VITE_APP_API_URL + `/api/portfolios/${portfolio_id}/initiatives/`;
    const response: AxiosResponse = await axiosInstance.get(url, { signal });
    return response.data;
  } catch (error: unknown) {
    // Re-throw axios cancellation errors unchanged so TanStack Query can
    // recognize them as cancellations rather than legitimate failures.
    if (isCancel(error)) {
      return Promise.reject(error);
    }
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching initiatives.'));
    }
  }
}

/**
 * Fetches KPI overview chart data for initiatives in a portfolio
 *
 * @param portfolio_id - portfolio id
 * @param cache_bust - whether to cache bust the request
 * @returns KPI overview data for initiatives visualization
 * @throws {AxiosError} When the API request fails
 * @throws {Error} When an unknown error occurs
 */
async function getInitiativesChartData(portfolio_id: string, cache_bust = true): Promise<KPIOverviewData> {
  try {
    const url = appConfig.VITE_APP_API_URL + `/api/portfolios/${portfolio_id}/initiatives/chart-data/`;
    const response: AxiosResponse = await axiosInstance.get(url, { params: { cache_bust } });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while fetching initiatives chart data.'));
    }
  }
}

/**
 * Fetches initiative object for a given portfolio and initiative ids
 * @param portfolio_id - portfolio id
 * @param initiative_id - initiative id
 * @returns Initiative object
 */
async function getInitiative(portfolio_id: string, initiative_id: string): Promise<ExtendedInitiative> {
  try {
    const url = appConfig.VITE_APP_API_URL + `/api/portfolios/${portfolio_id}/initiatives/${initiative_id}/`;
    const response: AxiosResponse = await axiosInstance.get(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while fetching initiative ${initiative_id}`));
    }
  }
}

/**
 * Method to create a new initiative
 * @param portfolio_id portfolio id ot attach the initiative to
 * @param initiative initiative object to create
 * @returns created initiative object
 */
async function createInitiative(portfolio_id: string, initiative: PreSaveInitiative): Promise<Initiative> {
  try {
    const url = appConfig.VITE_APP_API_URL + `/api/portfolios/${portfolio_id}/initiatives/`;
    const response: AxiosResponse = await axiosInstance.post(url, initiative);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while creating initiative.'));
    }
  }
}

/**
 * Method to update the given initiative
 * @param portfolio_id portfolio id ot attach the initiative to
 * @param initiative updated initiative object
 * @returns updated initiative object
 */
async function updateInitiative(portfolio_id: string, initiative: PreSaveInitiative): Promise<Initiative> {
  try {
    const url = appConfig.VITE_APP_API_URL + `/api/portfolios/${portfolio_id}/initiatives/${initiative.id}/`;
    const response: AxiosResponse = await axiosInstance.put(url, initiative);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while updating initiative ${initiative.id}`));
    }
  }
}

/**
 * Method to partially update the given initiative
 * @param portfolio_id portfolio id ot attach the initiative to
 * @param initiative_id initiative id to update
 * @param data data to update
 * @returns updated initiative object
 */
async function partiallyUpdateInitiative(
  portfolio_id: string,
  initiative_id: string,
  data: Partial<PreSaveInitiative>,
): Promise<Initiative> {
  try {
    const url = appConfig.VITE_APP_API_URL + `/api/portfolios/${portfolio_id}/initiatives/${initiative_id}/`;
    const response: AxiosResponse = await axiosInstance.patch(url, data);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(
        new Error(`An unknown error occurred while partially updating initiative ${initiative_id}`),
      );
    }
  }
}

/**
 * Method to delete the given initiative
 * @param portfolio_id portfolio id of the initiative
 * @param initiative_id initiative id to delete
 * @returns status code 204 if successful
 */
async function deleteInitiative(portfolio_id: string, initiative_id: string): Promise<void> {
  try {
    const url = appConfig.VITE_APP_API_URL + `/api/portfolios/${portfolio_id}/initiatives/${initiative_id}/`;
    const response: AxiosResponse = await axiosInstance.delete(url);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error('An unknown error occurred while deleting portfolio.'));
    }
  }
}

/**
 * Fetches focus data for an initiative
 * @param portfolio_id - portfolio id
 * @param initiative_id - initiative id
 * @param scope - scope to fetch focus for: 'project', 'subproject'
 * @returns Focus data object
 */
async function getInitiativeFocus(
  portfolio_id: string,
  initiative_id: string,
  scope: 'project' | 'subproject',
): Promise<FocusData> {
  try {
    const url = new URL(
      `${appConfig.VITE_APP_API_URL}/api/portfolios/${portfolio_id}/initiatives/${initiative_id}/focus/`,
    );
    const params = new URLSearchParams({ scope });
    url.search = params.toString();
    const response: AxiosResponse = await axiosInstance.get(url.toString());
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(
        new Error(`An unknown error occurred while fetching focus data for initiative ${initiative_id}`),
      );
    }
  }
}

/**
 * Method to get initiative tasks
 * @param portfolioId portfolio id of the initiative
 * @param initiativeId initiative id
 * @param filter filter to apply
 * @param date date to filter by
 * @param epicId epic id to filter by
 * @param projectId project id to filter by
 * @returns array of InitiativeTaskResponse objects
 */
async function getInitiativeTasks({
  portfolioId,
  initiativeId,
  filter,
  date,
  epicId,
  projectId,
}: {
  portfolioId: string;
  initiativeId: string;
  filter: InitiativeTasksFilters;
  date: string | undefined;
  epicId: string | undefined;
  projectId: string | undefined;
}): Promise<InitiativeTaskResponse[]> {
  try {
    const url = new URL(
      appConfig.VITE_APP_API_URL + `/api/portfolios/${portfolioId}/initiatives/${initiativeId}/tasks/`,
    );
    const params = new URLSearchParams(url.search);
    params.append('filter', filter);
    if (date) {
      params.append('date', date);
    }
    if (epicId) {
      params.append('epic_id', epicId);
    }
    if (projectId) {
      params.append('project_id', projectId);
    }
    url.search = params.toString();
    const response: AxiosResponse = await axiosInstance.get(url.toString());
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return Promise.reject(error.response);
    } else {
      return Promise.reject(new Error(`An unknown error occurred while fetching tasks for initiative ${initiativeId}`));
    }
  }
}

export {
  createInitiative,
  deleteInitiative,
  getInitiative,
  getInitiativeFocus,
  getInitiatives,
  getInitiativesChartData,
  getInitiativeTasks,
  partiallyUpdateInitiative,
  updateInitiative,
};
